import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useElementListMaxValues } from '@features/proposals/use-proposals.hooks';
import {
  useElementResults,
  useFilterResultsBySelectedLifecycles,
  useGetResultsPerGFA,
} from '@features/results/use-results.hooks';
import { IElement } from '@shared/models/project.model';
import { getProject as _getProject } from '@client/store/project/project.hook';
import {
  menuMouseHandler,
  openMenu,
  useIsMenuOpen,
} from '@client/hooks/use-menu.hooks';
import {
  type ElementListUrlPage,
  useNavigateToElementListSelection,
} from '@client/hooks/use-router.hooks';
import { useOpenFocusElement } from '@client/hooks/use-element-focus.hook';
import { ElementKebabMenu } from '@features/projects/menus/ElementKebabMenu';
import { getElementCategory } from '@shared/helpers/element-category.helpers';
import { isProductCategoryId } from '@shared/models/element-categories.model';
import { getElementClimateTag } from '@shared/helpers/product-element.helpers';
import { useSelectedVersionProductsAndStoreProducts } from '@client/store/ui';
import { ProductElementClimateTag } from '@features/products/components/ProductElementClimateTag';
import ElementListRow, {
  ElementListRowHandle,
  ElementListRowProps,
} from './ElementListRow';
import { afterNextPaint } from './element-list-scroll.helpers';
import { consumeScrollRequest } from './element-scroll-request.store';
import { withLiveProductA1A3 } from './element-list-live-co2.helpers';
import { useLiveProductA1A3ByElementId } from './use-element-list-live-co2.hook';
import { useElementListRowProposalBars } from './use-element-list-row-proposal-bars.hooks';
import { useElementRowEditing } from './use-element-row-editing.hook';

type PassThroughProps = Pick<
  ElementListRowProps,
  | 'depth'
  | 'variant'
  | 'groupNumber'
  | 'expandable'
  | 'expanded'
  | 'onExpandedChange'
  | 'selected'
  | 'onSelect'
>;

interface ElementListRowConnectedProps extends PassThroughProps {
  element: IElement;
  disabled?: boolean;
  /** When set, written to the URL on select (e.g. actions tab → {@link Page.Elements}). */
  selectionPage?: ElementListUrlPage;
}

const ElementListRowConnected: React.FC<ElementListRowConnectedProps> = ({
  element,
  disabled,
  selectionPage,
  ...passThroughProps
}) => {
  const { maxCo2: co2ePerGfaMax, maxCost: costPerGfaMax } =
    useElementListMaxValues();
  const getResultsPerGFA = useGetResultsPerGFA();
  // For versions, useElementResults returns the element's results resolved
  // against the selected proposal (or baseline when none).
  const elementResults = useElementResults(element, undefined, false);
  const filterResults = useFilterResultsBySelectedLifecycles();
  const liveProductA1A3 = useLiveProductA1A3ByElementId();
  // The side panel multiplies resolved product counts immediately. Stored
  // results stay on the previous total until enrich rewrites them (DEV-3937).
  const resultsForBars = useMemo(
    () =>
      withLiveProductA1A3(
        elementResults,
        liveProductA1A3.get(element.id),
        filterResults,
      ),
    [elementResults, element.id, liveProductA1A3, filterResults],
  );
  const openFocusElement = useOpenFocusElement();
  const rowDomRef = useRef<HTMLDivElement>(null);
  const rowHandle = useRef<ElementListRowHandle>(null);
  // After paint so ElementsView's tab scroll-restore layout effect settles first.
  useEffect(() => {
    const row = rowDomRef.current;
    if (!row) return;
    return afterNextPaint(() => {
      const request = consumeScrollRequest(element.id);
      if (!request) return;
      row.scrollIntoView({ behavior: 'auto', block: request.align });
    });
  }, [element.id]);

  const handleKebabClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.nativeEvent.stopPropagation();
      const anchorPosition = menuMouseHandler(e);
      if (anchorPosition) {
        openMenu({ anchorPosition, id: element.id });
      }
    },
    [element.id],
  );
  const kebabMenuOpen = useIsMenuOpen(element.id);
  const navigateToListSelection = useNavigateToElementListSelection();

  const editing = useElementRowEditing(element);
  const { displayElement, readonly } = editing;

  const proposalBars = useElementListRowProposalBars({
    element,
    elementResults: resultsForBars,
    getResultsPerGFA,
  });

  const productLookup = useSelectedVersionProductsAndStoreProducts();
  const climateTag = useMemo(
    () => getElementClimateTag(displayElement, productLookup),
    [displayElement, productLookup],
  );

  const category = getElementCategory(displayElement);

  // Editing is locked when the row is read-only (shared routes / permissions) or
  // deactivated (self or an ancestor). Locked rows still allow selection and
  // focus/open, but no inline name/quantity/unit editing.
  const isDeactivated = disabled || editing.isDeactivated;
  const lockEditing = readonly || isDeactivated;

  return (
    <div ref={rowDomRef}>
      <ElementListRow
        ref={rowHandle}
        {...passThroughProps}
        scrollTo={passThroughProps.selected}
        onSelect={(e) => {
          passThroughProps.onSelect?.(e);
          navigateToListSelection(element.id, { page: selectionPage });
        }}
        name={editing.name}
        namePlaceholder={editing.namePlaceholder}
        quantityValue={editing.quantityValue}
        quantityDisplayValue={editing.quantityDisplayValue}
        quantityDisplayNumber={editing.quantityDisplayNumber}
        quantityPlaceholder={editing.quantityPlaceholder}
        quantityFillValue={editing.quantityFillValue}
        unit={editing.unit}
        quantityDisplayUnit={editing.quantityDisplayUnit}
        proposals={proposalBars}
        maxCo2={co2ePerGfaMax || 1}
        maxCost={costPerGfaMax || 1}
        nameTrailingSlot={
          climateTag ? <ProductElementClimateTag tag={climateTag} /> : undefined
        }
        categoryIcon={editing.categoryIcon}
        onNameSave={editing.handleNameSave}
        onQtySave={editing.handleQtySave}
        onQtyFxClick={lockEditing ? undefined : editing.onQtyFxClick}
        onFocusClick={
          !isProductCategoryId(category?.id ?? '')
            ? () => openFocusElement(element.id)
            : undefined
        }
        onKebabClick={readonly ? undefined : handleKebabClick}
        kebabMenuOpen={kebabMenuOpen}
        nameReadonly={lockEditing}
        quantityReadonly={lockEditing}
        onUnitClick={lockEditing ? undefined : editing.onUnitClick}
        disabled={isDeactivated}
        showDisabledIcon={editing.isDeactivated}
      />
      {editing.unitMenu}
      <span style={{ display: 'none' }}>
        <ElementKebabMenu
          element={element}
          resolvedElement={displayElement}
          hidden
          onRename={() => rowHandle.current?.startRename()}
        />
      </span>
    </div>
  );
};

export default React.memo(ElementListRowConnected);
